import { useProject } from '../project-hooks/useProject';
import {
    getProjectCommentsWithoutTransaction,
    getProjectTransactions,
    joinAnonymousProjectTransactions,
    mergeAndSortActivity,
    transactionIsAnonymous,
} from '../../transaction/transaction-reducer';
import { useDispatch, useSelector } from 'react-redux';
import { useFeature } from '../../components/hooks/isFeatureEnabled';
import { PROJECT_LATEST_ACTIVITY_IS_DISABLED } from '../../constants/features/constants';
import { useProjectTimeline } from '../project-timeline/useProjectTimeline';
import { useSubproject } from '../../components/hooks/useSubproject';
import TrackEventLink from '../../components/track-event-link/TrackEventLink';
import { isMobile, pluralize } from '../../utils';
import { openActivityModal } from '../project-activities-actions';
import { trackEvent } from '../../support-and-tracking/analytics-actions';
import styles from './ProjectLatestActivity.module.less';
import { useI18nTexts } from '../i18n/useI18nTexts';
import {
    DONOR_TEXT_KEY,
    DONORS_TEXT_KEY,
    HAS_DONATED_ANONYMOUS_TEXT_KEY,
    LATEST_ACTIVITY_TEXT_KEY,
    SEE_ALL_ACTIVITY_TEXT_KEY,
    SEE_ALL_MEMORY_COMMENTS_TEXT_KEY,
} from '../i18n/project-i18n-model';
import ActivityCard from '../activity-card/ActivityCard';
import { getUserAgent } from '../../utils/browserDetector';
import { giversMustBeAnonymous, isMinnespleis } from '../project-model';
import { Heading2 } from '@sb1/ffe-core-react';

const ProjectLatestActivity = ({ className = '', title, linkToHistory = true, limit }) => {
    const dispatch = useDispatch();
    const project = useProject();
    const { subprojectId } = useSubproject();
    const canShowHistory = useProjectTimeline(project);
    const texts = useI18nTexts([
        LATEST_ACTIVITY_TEXT_KEY,
        SEE_ALL_ACTIVITY_TEXT_KEY,
        SEE_ALL_MEMORY_COMMENTS_TEXT_KEY,
        DONOR_TEXT_KEY,
        DONORS_TEXT_KEY,
        HAS_DONATED_ANONYMOUS_TEXT_KEY,
    ]);
    const isProjectAMinnespleis = isMinnespleis(project);
    const transactions = useSelector((state) => getProjectTransactions(state, project?.id, subprojectId)) ?? [];
    const commentsWithoutTransaction = useSelector((state) => getProjectCommentsWithoutTransaction(state, project?.id));
    const latestActivityIsDisabled = useFeature(PROJECT_LATEST_ACTIVITY_IS_DISABLED) || !canShowHistory;

    const activityItems = joinAnonymousProjectTransactions(
        isProjectAMinnespleis ? mergeAndSortActivity(transactions, commentsWithoutTransaction) : transactions
    );
    const transactionsFiltered = limit ? activityItems.slice(0, limit) : activityItems;
    const userAgent = useSelector((state) => getUserAgent(state));

    if (transactionsFiltered?.length === 0 || latestActivityIsDisabled) {
        return null;
    }

    let transactionElements;
    if (giversMustBeAnonymous({ project })) {
        const numberOfCowards = project.giver_count;
        const anonymousActionText = `${numberOfCowards} ${pluralize(numberOfCowards, texts[DONOR_TEXT_KEY], texts[DONORS_TEXT_KEY])} ${texts[HAS_DONATED_ANONYMOUS_TEXT_KEY]}`;
        const firstTransaction = transactionsFiltered[0];
        transactionElements = [
            <li className={styles.activity} key={firstTransaction?.id}>
                <ActivityCard
                    transaction={{
                        anonymous: true,
                        createdAt: firstTransaction?.createdAt,
                    }}
                    anonymousActionText={anonymousActionText}
                />
            </li>,
        ];
    } else {
        transactionElements = transactionsFiltered.map((transaction) => {
            const key = transaction.id ?? transaction.created_at;
            if (transactionIsAnonymous(transaction)) {
                const numberOfCowards = transaction.cowards?.length;
                const firstCoward = transaction.cowards?.[numberOfCowards - 1];
                const anonymousActionText = `${numberOfCowards} ${pluralize(numberOfCowards, texts[DONOR_TEXT_KEY], texts[DONORS_TEXT_KEY])} ${texts[HAS_DONATED_ANONYMOUS_TEXT_KEY]}`;
                return (
                    <li className={styles.activity} key={`${key}-${firstCoward?.id || firstCoward?.created_at}`}>
                        <ActivityCard transaction={transaction} anonymousActionText={anonymousActionText} />
                    </li>
                );
            }

            return (
                <li className={styles.activity} key={key}>
                    <ActivityCard transaction={transaction} comment={transaction.comment} />
                </li>
            );
        });
    }

    return (
        <div className={[styles.latestActivity, className].join(' ')}>
            <Heading2 lookLike={4}>{title || texts[LATEST_ACTIVITY_TEXT_KEY]}</Heading2>
            <ol className={styles.activities}>{transactionElements}</ol>
            {linkToHistory && canShowHistory && !giversMustBeAnonymous({ project }) && (
                <TrackEventLink
                    category={'project-latest-activity'}
                    action={'click'}
                    to={`/project/${project.id}/history#project-timeline`}
                    onClick={(e) => {
                        if (isMobile(userAgent)) {
                            e.preventDefault();
                            dispatch(openActivityModal());
                            trackEvent('project-latest-activity', 'click', { 'project-id': project.id });
                        }
                    }}
                    preventScrollReset={true}
                    className={styles.allActivities}
                >
                    {texts[isProjectAMinnespleis ? SEE_ALL_MEMORY_COMMENTS_TEXT_KEY : SEE_ALL_ACTIVITY_TEXT_KEY]}
                </TrackEventLink>
            )}
        </div>
    );
};

export default ProjectLatestActivity;
